import { ChangeDetectorRef, Directive, Input, OnChanges, ViewRef } from '@angular/core';
import { Product } from '@df/catalog/product/product';
import { Review } from '@df/catalog/review/review';
import { App } from '@df/core/app';
import { Status } from '@df/core/status';
import take from 'lodash-es/take';
import { Subscription } from 'rxjs';

@Directive()
export class ProductReviewsComponentDef implements OnChanges {
    /**
     * Reviews limit to load
     */
    @Input()
    limit?: number;

    /**
     * If there are more to load
     */
    hasMore = true;

    /**
     * Source products to find reviews for
     */
    @Input()
    product?: number | (number | Product)[] | Product;

    /**
     * Reviews items
     */
    reviews: Review[] | undefined;

    /**
     * Product reviews load status
     */
    status = new Status();

    protected subscriptions: Subscription = new Subscription();

    private hash?: string;

    /**
     * class constructor
     */
    constructor(
        protected _ref: ChangeDetectorRef,
        private app: App
    ) {}

    ngOnInit() {
        this.subscriptions.add(this.status.subscribe(() => this.detectChanges()));
    }

    /**
     * Oninput changes pulls reviews to show
     */
    ngOnChanges(): void {
        const products = this.getProductIds();
        const hash = JSON.stringify([products, this.limit]);

        if (!this.hash || this.hash !== hash) {
            this.hash = hash;

            this.loadReviewsForProducts(products, this.limit);
        }
    }

    ngOnDestroy() {
        this.subscriptions.unsubscribe();
    }

    trackById(index: number, review: Review): string | number {
        return review.id;
    }

    protected getProductIds(): number[] {
        return this.product ? this.app.resources.product.toIds(this.product).sort() : [];
    }

    protected loadReviewsForProducts(products: number[], limit?: number): void {
        if (products?.length) {
            this.app.request.send(this.status, async () => {
                try {
                    const reviews = await this.app.resources.review.loadByProduct(products, { verbosity: 3, limit });
                    this.setReviews(reviews);
                } catch (err) {
                    this.app.logger.error(err);
                    this.setReviews();
                }
            });
        } else {
            this.resetReviews();
        }
    }

    protected resetReviews(): void {
        this.hash = undefined;
        
        if (this.status?.promise?.cancel) {
            this.status.promise.cancel();
            this.status.reset();
        }

        this.setReviews();
    }

    /**
     * Reset reviews
     */
    protected setReviews(reviews?: Review[]): void {
        this.reviews = this.limit ? take(reviews, this.limit) : reviews;
        this.hasMore = this.limit && this.reviews && this.reviews.length === this.limit ? true : false;

        this.detectChanges();
    }

    detectChanges(): void {
        if (!(<ViewRef>this._ref).destroyed) {
            this._ref.detectChanges();
        }
    }
}
